import React, { useState, useEffect } from 'react';
import axios from 'axios';

function VendorManagement() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    businessName: '',
    businessNameAr: '',
    specializations: '',
    preferredContactMethod: 'phone',
    workingHours: {
      start: '09:00',
      end: '18:00'
    },
    isActive: true,
    isVerified: false,
    priority: 0,
    notes: ''
  });

  useEffect(() => {
    fetchVendors();
  }, [filterActive]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const url = filterActive === 'all' ? '/api/vendors' : `/api/vendors?isActive=${filterActive === 'active'}`;
      const response = await axios.get(url);
      setVendors(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError('Failed to load vendors');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      businessName: '',
      businessNameAr: '',
      specializations: '',
      preferredContactMethod: 'phone',
      workingHours: {
        start: '09:00',
        end: '18:00'
      },
      isActive: true,
      isVerified: false,
      priority: 0,
      notes: ''
    });
    setEditingVendor(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        specializations: formData.specializations.split(',').map(s => s.trim()).filter(s => s)
      };

      if (editingVendor) {
        await axios.put(`/api/vendors/${editingVendor._id}`, dataToSubmit);
        alert('Vendor updated successfully!');
      } else {
        await axios.post('/api/vendors', dataToSubmit);
        alert('Vendor added successfully!');
      }

      resetForm();
      fetchVendors();
    } catch (err) {
      console.error('Error saving vendor:', err);
      alert('Failed to save vendor: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (vendor) => {
    setFormData({
      name: vendor.name,
      phone: vendor.phone,
      email: vendor.email || '',
      businessName: vendor.businessName || '',
      businessNameAr: vendor.businessNameAr || '',
      specializations: vendor.specializations?.join(', ') || '',
      preferredContactMethod: vendor.preferredContactMethod || 'phone',
      workingHours: {
        start: vendor.workingHours?.start || '09:00',
        end: vendor.workingHours?.end || '18:00'
      },
      isActive: vendor.isActive,
      isVerified: vendor.isVerified,
      priority: vendor.priority || 0,
      notes: vendor.notes || ''
    });
    setEditingVendor(vendor);
    setShowAddForm(true);
  };

  const handleDelete = async (vendorId, vendorName) => {
    if (window.confirm(`Are you sure you want to delete vendor "${vendorName}"?`)) {
      try {
        await axios.delete(`/api/vendors/${vendorId}`);
        alert('Vendor deleted successfully!');
        fetchVendors();
      } catch (err) {
        console.error('Error deleting vendor:', err);
        alert('Failed to delete vendor: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const toggleVendorStatus = async (vendorId, currentStatus) => {
    try {
      await axios.put(`/api/vendors/${vendorId}`, { isActive: !currentStatus });
      fetchVendors();
    } catch (err) {
      console.error('Error toggling vendor status:', err);
      alert('Failed to update vendor status');
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      vendor.name?.toLowerCase().includes(searchLower) ||
      vendor.businessName?.toLowerCase().includes(searchLower) ||
      vendor.phone?.includes(searchTerm) ||
      vendor.email?.toLowerCase().includes(searchLower) ||
      vendor.specializations?.some(s => s.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading vendors...</p>
      </div>
    );
  }

  return (
    <div className="vendor-management">
      <div className="vendor-header">
        <h2>🏪 Vendor Management</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          + Add New Vendor
        </button>
      </div>

      {(showAddForm || editingVendor) && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</h3>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="vendor-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Vendor name"
                  />
                </div>

                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="+971501234567"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="vendor@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Business Name (English)</label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    placeholder="Company name in English"
                  />
                </div>
                <div className="form-group">
                  <label>Business Name (Arabic)</label>
                  <input
                    type="text"
                    name="businessNameAr"
                    value={formData.businessNameAr}
                    onChange={handleInputChange}
                    placeholder="اسم الشركة بالعربية"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Specializations (comma-separated)</label>
                <input
                  type="text"
                  name="specializations"
                  value={formData.specializations}
                  onChange={handleInputChange}
                  placeholder="Engine Parts, Body Parts, Electronics"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Preferred Contact Method</label>
                  <select
                    name="preferredContactMethod"
                    value={formData.preferredContactMethod}
                    onChange={handleInputChange}
                  >
                    <option value="phone">Phone</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <input
                    type="number"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Working Hours Start</label>
                  <input
                    type="time"
                    name="workingHours.start"
                    value={formData.workingHours.start}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Working Hours End</label>
                  <input
                    type="time"
                    name="workingHours.end"
                    value={formData.workingHours.end}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Additional notes about the vendor"
                />
              </div>

              <div className="form-row checkbox-row">
                <div className="form-group-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                    <span>Active</span>
                  </label>
                </div>

                <div className="form-group-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="isVerified"
                      checked={formData.isVerified}
                      onChange={handleInputChange}
                    />
                    <span>Verified</span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingVendor ? 'Update Vendor' : 'Add Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="vendor-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search vendors by name, business, phone, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filters">
          <button 
            className={filterActive === 'all' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => setFilterActive('all')}
          >
            All
          </button>
          <button 
            className={filterActive === 'active' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => setFilterActive('active')}
          >
            Active
          </button>
          <button 
            className={filterActive === 'inactive' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => setFilterActive('inactive')}
          >
            Inactive
          </button>
        </div>
      </div>

      <div className="vendors-list">
        {filteredVendors.length === 0 ? (
          <div className="no-vendors">
            <p>No vendors found</p>
          </div>
        ) : (
          <div className="vendors-grid">
            {filteredVendors.map((vendor) => (
              <div key={vendor._id} className="vendor-card">
                <div className="vendor-card-header">
                  <div>
                    <h3>{vendor.name}</h3>
                    {vendor.businessName && (
                      <p className="business-name">{vendor.businessName}</p>
                    )}
                  </div>
                  <div className="vendor-badges">
                    {vendor.isVerified && (
                      <span className="badge badge-verified">✓ Verified</span>
                    )}
                    <span className={`badge badge-${vendor.isActive ? 'active' : 'inactive'}`}>
                      {vendor.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="vendor-card-body">
                  <div className="vendor-info">
                    <p><strong>📞 Phone:</strong> {vendor.phone}</p>
                    {vendor.email && <p><strong>📧 Email:</strong> {vendor.email}</p>}
                    <p><strong>💬 Contact:</strong> {vendor.preferredContactMethod}</p>
                    {vendor.workingHours && (
                      <p><strong>🕒 Hours:</strong> {vendor.workingHours.start} - {vendor.workingHours.end}</p>
                    )}
                    {vendor.priority > 0 && (
                      <p><strong>⭐ Priority:</strong> {vendor.priority}</p>
                    )}
                  </div>

                  {vendor.specializations && vendor.specializations.length > 0 && (
                    <div className="specializations">
                      <strong>Specializations:</strong>
                      <div className="tags">
                        {vendor.specializations.map((spec, idx) => (
                          <span key={idx} className="tag">{spec}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {vendor.notes && (
                    <div className="vendor-notes">
                      <strong>Notes:</strong>
                      <p>{vendor.notes}</p>
                    </div>
                  )}
                </div>

                <div className="vendor-card-footer">
                  <button
                    className="btn-icon"
                    onClick={() => handleEdit(vendor)}
                    title="Edit vendor"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => toggleVendorStatus(vendor._id, vendor.isActive)}
                    title={vendor.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {vendor.isActive ? '🔴' : '🟢'}
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(vendor._id, vendor.name)}
                    title="Delete vendor"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VendorManagement;
